import * as React from 'react';
import { LProps, withLocalization } from '../utils/wrappers';
import RouteLink from './RouteLink';
import { Routes } from '../utils/routerHelper';
import { isProduction } from '../utils/environment';
import * as moment from 'moment';
import { compose, bindActionCreators, Dispatch } from 'redux';
import { State } from '../redux/state';
import { connect } from 'react-redux';
import { Competition } from '../userApi';
import cx from 'classnames';

const Separator = () => (<span>&nbsp;&middot;&nbsp;</span>);

interface Props {
  competition: Competition;

}

interface FooterRouteLinkProps {
  route?: Routes;
  label: string;
  url?: string;
}

const FooterRouteLink = ({ route, label, url }: FooterRouteLinkProps) => {
  if (url) {
    return (
      <span>
        <a
          className={cx({ inactive: true })}
          href={url}
          target="__blank"
          rel="noopener noreferrer"
        > {label}
        </a>
        <span>&nbsp;·&nbsp;</span>
      </span>
    );
  }
  return (
    <><RouteLink route={route}>{label}</RouteLink><Separator /></>
  );
};

/** CCSF / SF Environment footer links. Same-origin Challenge routes stay on the
 * current host (staging vs production); SF Environment links are absolute. */
const ccsfFooterLinks = (origin: string, competitionName: string): Array<{ label: string; url: string }> => {
  const base = `${origin.replace(/\/$/, '')}/${competitionName}`;
  return [
    { label: 'About', url: `${base}/about-sf` },
    { label: 'Events', url: 'https://www.sfenvironment.org/events' },
    { label: 'Volunteer', url: 'https://www.sfenvironment.org/volunteer' },
    { label: 'Residents', url: 'https://www.sfenvironment.org/resident-resources' },
    { label: 'Businesses', url: 'https://www.sfenvironment.org/business-resources' },
    { label: 'City Staff', url: 'https://www.sfenvironment.org/city-staff-resources' },
  ];
};

const Footer = ({
  t, competition
}: LProps<Props>) => {

  // Custom blog
  const blogLabel = (competition && competition.config && competition.config.blogLabel) || t('Blog');
  const blogUrl = (competition && competition.config && competition.config.blogUrl) || '';

  // Custom about
  const aboutLabel = (competition && competition.config && competition.config.aboutLabel) || t('About');
  let aboutUrl = (competition && competition.config && competition.config.aboutUrl) || '';

  // Custom Spread the word
  const spreadTheWordLabel = (competition && competition.config && competition.config.spreadTheWordLabel) || t('Spread the word');
  const spreadTheWordUrl = (competition && competition.config && competition.config.spreadTheWordUrl) || 'https://sites.google.com/view/2019ccc/materials-for-download';

  // Custom Terms of use
  const termsOfUseLabel = (competition && competition.config && competition.config.termsOfUseLabel) || t('Terms of use');
  const termsOfUseUrl = (competition && competition.config && competition.config.termsOfUseUrl) || '/assets/coolcampuschallenge2019_terms_and_conditions.pdf';
  // Disable Terms of use
  const disableTermsOfUse = (competition && competition.config && competition.config.termsOfUseDisable) || false;
  const customLinksLabels = (competition && competition.config && competition.config.customLinksLabels) || [];
  const customLinksUrls = (competition && competition.config && competition.config.customLinksUrls) || [];
  // Custom copyright
  const copyRightLabel = (competition && competition.config && competition.config.copyRightLabel) || 'Regents of the University of California';
  let aboutRoute = Routes.About;
  if (aboutUrl === 'about:sf' || aboutUrl === 'about:ccsf') {
    aboutRoute = Routes.AboutSF;
    aboutUrl = '';
  }

  const useCcsfFooter = Boolean(
    competition &&
    competition.config &&
    (competition.config.templateSuffix === 'sf' || competition.name === 'ccsf')
  );
  const ccsfLinks = useCcsfFooter
    ? ccsfFooterLinks(
        typeof window !== 'undefined' ? window.location.origin : 'https://coolcampuschallenge.org',
        (competition && competition.name) || 'ccsf'
      )
    : [];

  return (
    <footer id="cc__footer">
      <div>
        {useCcsfFooter ? (
          ccsfLinks.map((link, index) => (
            <FooterRouteLink key={`${link.label}-${index}`} label={link.label} url={link.url} />
          ))
        ) : (
          <>
            {competition && <FooterRouteLink route={aboutRoute} label={aboutLabel} url={aboutUrl} />}
            {!competition && <><RouteLink route={Routes.GlobalAbout}>{t('About')}</RouteLink><Separator /></>}
            {/* Hide Blog/Resources when no URL is configured (CCSF does not run a blog). */}
            {competition && Boolean(blogUrl) && <FooterRouteLink route={Routes.Blog} label={blogLabel} url={blogUrl} />}
            {competition && <><RouteLink route={Routes.Calculator}>{t('Calculator')}</RouteLink><Separator /></>}
            {competition && <><RouteLink route={Routes.Accessibility}>{t('Accessibility')}</RouteLink><Separator /></>}

            {/* tslint:disable-next-line react-a11y-anchors */}
            {Boolean(spreadTheWordUrl) && (
              <a href={spreadTheWordUrl} target="_blank" rel="noopener noreferrer">{spreadTheWordLabel} </a>
            )}

            {/* tslint:disable-next-line react-a11y-anchors */}

            {!disableTermsOfUse &&
              <>
                <Separator />
                <a
                  href={termsOfUseUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={true}
                > {termsOfUseLabel}</a>
              </>
            }
            {customLinksLabels && customLinksLabels.length > 0 &&
              <>
                <Separator />
                {customLinksLabels.map((label, index) => (
                  <FooterRouteLink key={`${label}-${index}`} url={customLinksUrls[index]} label={label} />
                )
                )}
              </>
            }
          </>
        )}
      </div>
      <div id="cc__footer__copyright">
        &copy; {moment().format('YYYY')} {copyRightLabel}
      </div>
    </footer>
  );
};

const mapStateToProps = (state: State): Partial<Props> => ({
  competition: state.competition
});

const mapDispatchToProps = (dispatch: Dispatch): Partial<{}> => bindActionCreators({

}, dispatch);

export default compose(
  withLocalization('footer'),
  connect(mapStateToProps, mapDispatchToProps)
)(Footer);
